import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Chip,
    IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { liveURL, IMAGE_URL } from '../constants/Consts';

const PostApproval = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [adminComment, setAdminComment] = useState('');
    const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
    const { authToken } = useAuth();

    const fetchPosts = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${liveURL}/v1/adminPosts/getAllPosts`, {
                headers: { token: authToken }
            });
            if (response.data.Result) {
                setPosts(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setLoading(false);
        }
    }, [authToken]);

    useEffect(() => {
        if (authToken) fetchPosts();
    }, [authToken, fetchPosts]);

    const handleActionClick = (post, type) => {
        setSelectedPost(post);
        setActionType(type);
        setAdminComment(post.admin_comment || '');
        setOpenDialog(true);
    };

    const handleConfirmAction = async () => {
        try {
            const status = actionType === 'approve' ? 'approved' : 'rejected';
            const response = await axios.put(
                `${liveURL}/v1/adminPosts/updateStatus/${selectedPost.id}`,
                { status, admin_comment: adminComment },
                { headers: { token: authToken } }
            );

            if (response.data.Result) {
                setOpenDialog(false);
                fetchPosts();
            }
        } catch (error) {
            console.error('Error updating post status:', error);
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'approved':
                return <Chip label="Approved" color="success" size="small" />;
            case 'rejected':
                return <Chip label="Rejected" color="error" size="small" />;
            default:
                return <Chip label="Pending" color="warning" size="small" />;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                Post Approval Management
            </Typography>

            <TableContainer component={Paper} sx={{ mt: 3, boxShadow: 3 }}>
                <Table>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell>Post</TableCell>
                            <TableCell>Title & Description</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Comment</TableCell>
                            <TableCell>Created At</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {posts.map((post) => (
                            <TableRow key={post.id} hover>
                                <TableCell>
                                    <img 
                                        src={`${IMAGE_URL}${post.image_url}`} 
                                        alt="post" 
                                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                                        onClick={() => window.open(`${IMAGE_URL}${post.image_url}`, '_blank')}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {post.title || <span style={{ color: '#9e9e9e', fontStyle: 'italic' }}>No Title</span>}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {post.description || 'No description'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{post.user_name}</Typography>
                                    <Typography variant="caption" color="textSecondary">{post.user_email}</Typography>
                                </TableCell>
                                <TableCell>{getStatusChip(post.status)}</TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {post.admin_comment || '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell>{new Date(post.created_at).toLocaleDateString()}</TableCell>
                                <TableCell align="center">
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                        <IconButton 
                                            color="success" 
                                            onClick={() => handleActionClick(post, 'approve')}
                                            disabled={post.status === 'approved'}
                                        >
                                            <CheckCircleIcon />
                                        </IconButton>
                                        <IconButton 
                                            color="error" 
                                            onClick={() => handleActionClick(post, 'reject')}
                                            disabled={post.status === 'rejected'}
                                        >
                                            <CancelIcon />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        {posts.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">No posts found.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Action Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>
                    {actionType === 'approve' ? 'Approve Post' : 'Reject Post'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to {actionType} this post?
                    </Typography>
                    {selectedPost && (
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                            {selectedPost.title && (
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    {selectedPost.title}
                                </Typography>
                            )}
                            {selectedPost.description && (
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                                    {selectedPost.description}
                                </Typography>
                            )}
                            <img 
                                src={`${IMAGE_URL}${selectedPost.image_url}`} 
                                alt="preview" 
                                style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 8 }}
                            />
                        </Box>
                    )}
                    <TextField
                        label="Admin Comment"
                        multiline
                        rows={3}
                        fullWidth
                        value={adminComment}
                        onChange={(e) => setAdminComment(e.target.value)}
                        placeholder="Add a comment for the user..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={handleConfirmAction} 
                        variant="contained" 
                        color={actionType === 'approve' ? 'success' : 'error'}
                    >
                        Confirm {actionType === 'approve' ? 'Approval' : 'Rejection'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PostApproval;
